/**
 * DocxDropzone — a reusable drag-and-drop / click-to-choose picker for a single
 * .docx file (MR Inc 4.6). Sibling of XlsxDropzone; used by the Client Reports
 * page for the page-1 Word review upload.
 *
 * Accepts a single Word document by MIME or .docx extension (the parent owns what
 * happens with the file; rejections call onReject for a friendly message).
 */
import { Group, Text } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconFileText, IconUpload, IconX } from '@tabler/icons-react';

/** MIME → allowed extensions (react-dropzone Accept shape): OOXML Word or .docx name. */
const DOCX_ACCEPT = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

interface DocxDropzoneProps {
  /** Called with the chosen file (the first, since multiple is off). */
  onSelect: (file: File) => void;
  /** Spinner + disabled while the parent processes the file. */
  loading?: boolean;
  /** Called when a drop is rejected (wrong type / more than one file). */
  onReject?: () => void;
}

export function DocxDropzone({ onSelect, loading, onReject }: DocxDropzoneProps) {
  return (
    <Dropzone
      onDrop={(files) => { if (files[0]) onSelect(files[0]); }}
      onReject={() => onReject?.()}
      accept={DOCX_ACCEPT}
      maxFiles={1}
      multiple={false}
      loading={loading}
    >
      <Group justify="center" gap="xl" mih={120} style={{ pointerEvents: 'none' }}>
        <Dropzone.Accept><IconUpload size={42} stroke={1.5} /></Dropzone.Accept>
        <Dropzone.Reject><IconX size={42} stroke={1.5} /></Dropzone.Reject>
        <Dropzone.Idle><IconFileText size={42} stroke={1.5} /></Dropzone.Idle>
        <div>
          <Text size="lg" inline>Drag the .docx review here, or click to choose</Text>
          <Text size="sm" c="dimmed" inline mt={7}>
            One Word file — the filled page-1 review template.
          </Text>
        </div>
      </Group>
    </Dropzone>
  );
}
